import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { CaptainAvatar } from "./CaptainAvatar";
import { CaptainEmptyState } from "./CaptainEmptyState";
import { CaptainInput } from "./CaptainInput";
import { CaptainMessageList } from "./CaptainMessageList";
import { CaptainSidebar } from "./CaptainSidebar";
import { fetchConversation } from "../api/useConversation";
import { useConversations } from "../api/useConversations";
import { useCaptainChat } from "../hooks/useCaptainChat";
import { useCaptainEnabled } from "../hooks/useCaptainEnabled";
import { closeCaptain, useCaptainPanel } from "../hooks/useCaptainPanel";

import { Button } from "@/components/ui/v2/button";
import { useAuth } from "@/lib/hooks/useAuth";

export function CaptainChatPanel() {
  const { isOpen } = useCaptainPanel();
  const { firstName, tenantId } = useAuth();
  const isAllowed = useCaptainEnabled();
  const { clientId } = useParams();
  const { t } = useTranslation();
  const [showSidebar, setShowSidebar] = useState(false);

  const {
    messages,
    conversationId,
    isStreaming,
    streamState,
    sendMessage,
    stopGenerating,
    loadConversation,
    reset,
  } = useCaptainChat(tenantId, clientId);

  const { data: conversations = [] } = useConversations(isOpen && isAllowed);

  if (!isOpen || !isAllowed) return null;

  const userName = firstName ?? "there";

  const handleSelectConversation = async (id: string) => {
    try {
      const conversation = await fetchConversation(id);
      loadConversation(id, conversation.messages);
      setShowSidebar(false);
    } catch {
      // Conversation may have been deleted
    }
  };

  const handleNewChat = () => {
    reset();
    setShowSidebar(false);
  };

  return (
    <div className="fixed bottom-0 right-0 z-50 flex h-[min(680px,100vh-80px)] w-[min(460px,100vw)] flex-col overflow-hidden border-l border-t border-border bg-background-alt shadow-2xl sm:bottom-4 sm:right-4 sm:rounded-xl sm:border">
      <AnimatePresence>
        {showSidebar && (
          <div className="absolute inset-0 z-10 flex">
            <motion.div
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="w-3/4 shrink-0"
            >
              <CaptainSidebar
                conversations={conversations}
                activeConversationId={conversationId}
                onSelectConversation={handleSelectConversation}
                onNewChat={handleNewChat}
                onClose={() => setShowSidebar(false)}
              />
            </motion.div>
            <div
              className="flex-1"
              onClick={() => setShowSidebar(false)}
            />
          </div>
        )}
      </AnimatePresence>

      <div className="flex items-center gap-3 border-b border-border px-3 py-2.5">
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={() => setShowSidebar(true)}
        >
          <Menu className="size-4" />
        </Button>

        <CaptainAvatar />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight">{t("captain.headerTitle")}</p>
          <p className="text-xs text-muted-foreground">{t("captain.headerSubtitle")}</p>
        </div>

        <Button
          variant="ghost"
          size="icon-sm"
          onClick={closeCaptain}
        >
          <X className="size-4" />
        </Button>
      </div>

      {messages.length === 0 && !isStreaming ? (
        <CaptainEmptyState userName={userName} />
      ) : (
        <CaptainMessageList
          key={conversationId ?? "new"}
          messages={messages}
          isStreaming={isStreaming}
          streamPhases={streamState.phases}
          streamText={streamState.textContent}
          streamDisplays={streamState.displays}
          streamThinkingContent={streamState.thinkingContent}
          streamThinkingDuration={streamState.thinkingDurationSeconds}
          groundednessReplacement={streamState.groundednessReplacement}
        />
      )}

      <CaptainInput onSend={sendMessage} onStop={stopGenerating} isStreaming={isStreaming} />
    </div>
  );
}
